import React, { useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import './BrandModal.css';

/**
 * Modal de marca TurnLink — componente propio (NO SweetAlert), neutro y reutilizable.
 * Pensado para ir reemplazando los swal poco a poco. Toma el acento de `--primary-color`
 * (que la página pública ya inyecta con el color del cliente); si no existe, cae al verde
 * TurnLink. Cierra con la X, el backdrop, ESC o el botón de acción.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - title?: string
 *  - eyebrow?: string           — etiqueta corta sobre el título (ej. "Aviso")
 *  - icon?: ReactNode           — ícono dentro de un badge de marca; centra el encabezado
 *  - children: cuerpo del modal
 *  - closeLabel?: string (default "Entendido")
 *  - dismissible?: boolean (default true) — permite cerrar con backdrop/ESC/X
 */
export const BrandModal = ({
  open,
  onClose,
  title,
  eyebrow,
  icon,
  children,
  closeLabel = 'Entendido',
  dismissible = true,
}) => {
  const cardRef = useRef(null);

  const handleClose = useCallback(() => {
    if (dismissible && onClose) onClose();
  }, [dismissible, onClose]);

  // ESC para cerrar + bloquear el scroll del fondo mientras está abierto.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Enfocar el modal para accesibilidad de teclado.
    setTimeout(() => cardRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, handleClose]);

  if (!open) return null;

  // Con ícono, el encabezado se centra para un look de "anuncio" más premium.
  const centered = !!icon;

  return (
    <div
      className="brandmodal-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        className={`brandmodal-card${centered ? ' brandmodal-card--centered' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title || eyebrow || 'Aviso'}
        tabIndex={-1}
        ref={cardRef}
      >
        <span className="brandmodal-accent" aria-hidden="true" />

        {dismissible && (
          <button
            type="button"
            className="brandmodal-close"
            onClick={handleClose}
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        )}

        {icon && (
          <div className="brandmodal-badge" aria-hidden="true">
            <span className="brandmodal-badge-ring" />
            {icon}
          </div>
        )}

        {eyebrow && <span className="brandmodal-eyebrow">{eyebrow}</span>}

        {title && <h3 className="brandmodal-title">{title}</h3>}

        <div className="brandmodal-body">{children}</div>

        <div className="brandmodal-footer">
          <button type="button" className="brandmodal-btn" onClick={handleClose}>
            {closeLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BrandModal;
